import { ECycle } from 'store/field-options/types';
import optionErrors from 'lib/validators/option.errors';

export enum EInputField {
  COUNT,
  VOLUME,
  NOTICE,
}

// Volume must be a positive number
const volumeValidator = (n: number): boolean => n > 0;

// Notice must be a positive number
const noticeValidator = (n: number): boolean => n > 0;

/**
 * Validates a field using its name and value and returns an error
 * string if a problem is found
 *
 * @param fieldName Type of field
 * @param val Value of input
 * @returns Error string or null
 */
export function getOptionInputError(field: EInputField, val: number | null | undefined): string | null {
  if (val === undefined || val === null) {
    return optionErrors.invalidNumber;
  }

  if (Number.isNaN(val) || val <= 0) {
    return optionErrors.invalidNumber;
  }

  switch (field) {
    case EInputField.NOTICE: {
      return noticeValidator(val) ? null : optionErrors.invalidNotificationPeriod;
    }
    case EInputField.VOLUME: {
      return volumeValidator(val) ? null : optionErrors.invalidVolume;
    }
    default: {
      return optionErrors.invalidNumber;
    }
  }
}

/**
 * Determines if option count is valid based on criteria
 *
 * Requirements:
 *  Annually: 1-12
 *  Semi-Annually: 1-6
 *  Quarterly: 1-3
 *  Contract: 1-numberOfMonths
 *
 * @param cycle Option cycle chosen from dropdown
 * @param value Value of input field
 * @param numberOfMonths Number of months
 * @returns Boolean indicating whether value is valid or not
 */
export function getOptionCountErrors(cycle: ECycle, value: number | undefined, numberOfMonths?: number): string | null {
  // Verify whole number
  if (!Number.isInteger(value)) {
    return optionErrors.invalidOptionCountWholeNumber;
  }

  const optionAnnualValue = 13;
  const optionSemiAnnualValue = 7;
  const optionQuarterValue = 4;
  if (value === undefined) {
    return null;
  }
  switch (cycle) {
    case ECycle.ANNUAL: {
      return value > 0 && value < optionAnnualValue ? null : optionErrors.invalidOptionCountAnnually;
    }

    case ECycle.CONTRACT: {
      return numberOfMonths && value > 0 && value < numberOfMonths + 1 ? null : optionErrors.invalidOptionCountContract;
    }

    case ECycle.QUARTERLY: {
      return value > 0 && value < optionQuarterValue ? null : optionErrors.invalidOptionCountQuarterly;
    }

    case ECycle.SEMI_ANNUAL: {
      return value > 0 && value < optionSemiAnnualValue ? null : optionErrors.invalidOptionCountSemiAnnually;
    }

    default: {
      return null;
    }
  }
}
